import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { RESTAURANT } from "@/lib/restaurant";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/menu", label: "Carte" },
  { to: "/galerie", label: "Galerie" },
  { to: "/evenements", label: "Soirées" },
  { to: "/privatisation", label: "Privatisation" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      {/* Top bar with phone (always prominent) */}
      <div className="fixed top-0 inset-x-0 z-[60] bg-[#1F476B] text-white text-xs md:text-sm">
        <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-1.5 flex items-center justify-between gap-3">
          <a href={`tel:${RESTAURANT.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 hover:text-[#FFD7A6] transition" data-testid="topbar-phone">
            <Phone size={13} className="text-[#FFD7A6]" />
            <span className="hidden sm:inline">Réservation :</span>
            <b className="tracking-wide">{RESTAURANT.phone}</b>
          </a>
          <a href={`mailto:${RESTAURANT.email}`} className="hidden md:inline-flex items-center gap-2 hover:text-[#FFD7A6] transition" data-testid="topbar-email">
            <Mail size={13} className="text-[#FFD7A6]" />
            <span>{RESTAURANT.email}</span>
          </a>
          <span className="hidden lg:inline text-white/70">{RESTAURANT.fullAddress}</span>
        </div>
      </div>

      <header
        data-testid="site-header"
        className={`fixed top-7 inset-x-0 z-50 glass-header transition-all duration-300 ${scrolled ? "py-2" : "py-3"}`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-full bg-[#C04E38] text-white grid place-items-center font-serif-display text-lg shadow-md group-hover:rotate-[-6deg] transition-transform">G</div>
            <div className="leading-tight">
              <div className="font-serif-display text-xl text-[#2C2B29]">Globo Dancing</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#5C5A56]">{RESTAURANT.shortTagline} · Trappes</div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `btn-ghost text-[15px] ${isActive ? "text-[#C04E38]" : "text-[#2C2B29]"}`}
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/reserver" data-testid="header-reserve-btn" className="hidden sm:inline-flex btn-primary !px-6 !py-2.5 text-sm">Réserver</Link>
            <button onClick={() => setOpen((v) => !v)} className="xl:hidden btn-ghost !px-3 !py-2" aria-label="Menu" data-testid="mobile-menu-toggle">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="xl:hidden border-t border-[#E6E1D6]/60 bg-[#FDFBF7]/95 backdrop-blur-xl">
            <div className="max-w-[1440px] mx-auto px-6 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"}
                  className={({ isActive }) => `py-3 px-2 border-b border-[#E6E1D6]/60 ${isActive ? "text-[#C04E38]" : "text-[#2C2B29]"}`}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}>{n.label}</NavLink>
              ))}
              <Link to="/reserver" className="btn-primary mt-3 self-start" data-testid="mobile-reserve-btn">Réserver une table</Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
